import { useState, useEffect, useCallback, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "@hub/auth";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@hub/ui/components/ui/dialog";
import { Button } from "@hub/ui/components/ui/button";

// Default values (can be overridden via props or environment variables)
const DEFAULT_WARNING_SECONDS = 30;
const DEFAULT_CHECK_INTERVAL_SECONDS = 60;

interface SessionTimeoutDialogProps {
  /**
   * Called to validate if the current session is still valid.
   * Should return true if valid, false if expired/invalid.
   */
  validateSession: () => Promise<boolean>;
  /**
   * Called to attempt to extend the session.
   * Should return true if successful, false otherwise.
   */
  extendSession: () => Promise<boolean>;
  /**
   * Number of seconds to show the warning countdown before auto-logout.
   * Defaults to 30 seconds.
   */
  warningSeconds?: number;
  /**
   * How often to check if the session is still valid (in seconds).
   * Defaults to 60 seconds.
   */
  checkIntervalSeconds?: number;
}

export function SessionTimeoutDialog({
  validateSession,
  extendSession,
  warningSeconds = DEFAULT_WARNING_SECONDS,
  checkIntervalSeconds = DEFAULT_CHECK_INTERVAL_SECONDS,
}: SessionTimeoutDialogProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [countdown, setCountdown] = useState(warningSeconds);
  const [isExtending, setIsExtending] = useState(false);
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Convert check interval to milliseconds
  const checkIntervalMs = checkIntervalSeconds * 1000;

  // Handle logout and redirect
  const handleLogout = useCallback(async () => {
    setShowDialog(false);
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  // Handle user clicking "Stay Logged In"
  const handleStayLoggedIn = useCallback(async () => {
    setIsExtending(true);
    try {
      const success = await extendSession();
      if (success) {
        setShowDialog(false);
        setCountdown(warningSeconds);
      } else {
        // Extension failed, log out
        await handleLogout();
      }
    } catch {
      await handleLogout();
    } finally {
      setIsExtending(false);
    }
  }, [extendSession, handleLogout, warningSeconds]);

  // Start countdown when dialog is shown
  useEffect(() => {
    if (showDialog) {
      setCountdown(warningSeconds);
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Timer expired, log out
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      };
    }
  }, [showDialog, handleLogout, warningSeconds]);

  // Track showDialog in a ref so the session-check interval doesn't reset on dialog toggle
  const showDialogRef = useRef(showDialog);
  useEffect(() => {
    showDialogRef.current = showDialog;
  }, [showDialog]);

  // Periodically check if session is still valid
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const checkSession = async () => {
      // Don't check if dialog is already showing
      if (showDialogRef.current) {
        return;
      }

      try {
        const isValid = await validateSession();
        if (!isValid) {
          setShowDialog(true);
        }
      } catch {
        // Validation error, show dialog
        setShowDialog(true);
      }
    };

    // Initial check after a short delay
    const initialTimeout = setTimeout(checkSession, 5000);

    // Set up periodic checks
    checkIntervalRef.current = setInterval(checkSession, checkIntervalMs);

    return () => {
      clearTimeout(initialTimeout);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isAuthenticated, validateSession, checkIntervalMs]);

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Dialog open={showDialog} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <DialogTitle>Are you still there?</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            Your session is about to expire due to inactivity. Click "Stay
            Logged In" to continue your session.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center py-6">
          <div className="relative">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="44"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-200 dark:text-gray-700"
              />
              <circle
                cx="48"
                cy="48"
                r="44"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={2 * Math.PI * 44}
                strokeDashoffset={
                  2 * Math.PI * 44 * (1 - countdown / warningSeconds)
                }
                className="text-yellow-500 transition-all duration-1000 ease-linear"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {countdown}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-3 sm:gap-3">
          <Button
            variant="outline"
            onClick={handleLogout}
            disabled={isExtending}
            className="flex-1"
          >
            Log Out
          </Button>
          <Button
            onClick={handleStayLoggedIn}
            disabled={isExtending}
            className="flex-1"
          >
            {isExtending ? "Extending..." : "Stay Logged In"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
